import React, { useEffect, useState } from 'react';
import {
    Box, Button, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Paper,
    Select, SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import { format } from 'date-fns';
import ExcelJS from 'exceljs';
import { Project } from '../../types/projectTypes';
import { getActiveProjects } from '../../api/projectApi';
import { getUserProject } from '../../api/userApi';
import { profiles, ADMIN_SISTEMA } from '../../contexts/constants/profiles';
import { getConsolidatedUnproductivity, UnproductivityRow } from '../../api/consolidatedUnproductivityApi';
import logo from '../../assets/images/logoElecnor.png';

const formatDate = (date: string | null): string => {
    if (!date) return '';
    try {
        return format(new Date(date), 'dd-MM-yyyy');
    } catch {
        return '';
    }
};

const buildActivityLabel = (row: UnproductivityRow): string => {
    const sub = (row.subActivity || '').trim();
    return sub ? `${row.activityName} / ${sub}` : row.activityName;
};

const ConsolidatedUnproductivity: React.FC = () => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<number | undefined>(undefined);
    const [rows, setRows] = useState<UnproductivityRow[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [userRole, setUserRole] = useState<string>('');

    useEffect(() => { fetchProjects(); }, []);
    useEffect(() => {
        if (selectedProject) { fetchData(selectedProject); }
    }, [selectedProject]);

    const fetchProjects = async () => {
        try {
            const userStr = localStorage.getItem('user');
            let role = '';
            let rut = '';
            if (userStr) {
                const user = JSON.parse(userStr);
                role = user.rols?.description;
                rut = user.identificationNum;
                setUserRole(role);
            }
            let projectsData: Project[] = [];
            // Mismo criterio que los otros informes: Director / Gerente / Visualizador ven todos los activos.
            if (role === profiles[0].name || role === profiles[2].name || role === profiles[3].name || role === ADMIN_SISTEMA) {
                projectsData = await getActiveProjects();
            } else {
                const userProjects = await getUserProject(rut);
                projectsData = Array.isArray(userProjects) ? userProjects : [];
            }
            const valid = projectsData.filter((p) => p.status === undefined || p.status === true);
            setProjects(valid);
        } catch (error) {
            console.error('Error al cargar proyectos:', error);
            setProjects([]);
        }
    };

    const fetchData = async (projectId: number) => {
        setLoading(true);
        try {
            const data = await getConsolidatedUnproductivity(projectId);
            setRows(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error al cargar improductividades:', error);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    const handleProjectChange = (e: SelectChangeEvent<number>) => {
        setSelectedProject(Number(e.target.value));
    };

    const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Consolidado Improductividad');

        try {
            const imageResponse = await fetch(logo);
            const imageBuffer = await imageResponse.arrayBuffer();
            const imageId = workbook.addImage({ buffer: imageBuffer, extension: 'png' });
            worksheet.addImage(imageId, { tl: { col: 0, row: 0 }, ext: { width: 160, height: 60 } });
        } catch (e) {
            console.warn('No se pudo cargar el logo para la exportación');
        }

        const projectName = projects.find((p) => p.id === selectedProject);
        const titleCell = worksheet.getCell('C1');
        titleCell.value = `CONSOLIDADO DE IMPRODUCTIVIDAD${projectName ? ` — ${projectName.name?.toUpperCase()}` : ''}`;
        titleCell.font = { bold: true, size: 16, color: { argb: 'FF004F86' } };
        titleCell.alignment = { horizontal: 'left', vertical: 'middle' };

        worksheet.addRow([]);
        worksheet.addRow([]);

        const headers = ['Fecha', 'Actividad / Subactividad', 'Causa de Improductividad', 'Observaciones'];
        const headerRow = worksheet.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF004F86' } };
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        rows.forEach((row) => {
            const dataRow = worksheet.addRow([
                formatDate(row.date),
                buildActivityLabel(row),
                row.cause || '',
                row.observation || ''
            ]);
            dataRow.eachCell((cell) => {
                cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFEEEEEE' } },
                    left: { style: 'thin', color: { argb: 'FFEEEEEE' } },
                    bottom: { style: 'thin', color: { argb: 'FFEEEEEE' } },
                    right: { style: 'thin', color: { argb: 'FFEEEEEE' } }
                };
            });
        });

        worksheet.getColumn(1).width = 14;
        worksheet.getColumn(2).width = 30;
        worksheet.getColumn(3).width = 35;
        worksheet.getColumn(4).width = 60;

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `consolidado_improductividad_${format(new Date(), 'dd-MM-yyyy')}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const headerCellSx = { backgroundColor: '#004F86', color: '#fff', fontWeight: 'bold' as const };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" marginBottom={3} color="black" fontWeight="bold">
                Consolidado de Improductividad
            </Typography>

            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Proyecto</InputLabel>
                        <Select label="Proyecto" name="project" value={selectedProject ?? ''} onChange={handleProjectChange}>
                            {projects.map((project) => (
                                <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            <Box display="flex" mt={2}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="300px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper} sx={{ width: '100%', overflow: 'auto', maxHeight: 'calc(100vh - 240px)' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...headerCellSx, minWidth: 110 }}>Fecha</TableCell>
                                    <TableCell sx={{ ...headerCellSx, minWidth: 220 }}>Actividad / Subactividad</TableCell>
                                    <TableCell sx={{ ...headerCellSx, minWidth: 260 }}>Causa de Improductividad</TableCell>
                                    <TableCell sx={{ ...headerCellSx, minWidth: 320 }}>Observaciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                            {selectedProject ? 'No hay registros de improductividad para este proyecto.' : 'Seleccione un proyecto.'}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((row, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>{formatDate(row.date)}</TableCell>
                                            <TableCell>{buildActivityLabel(row)}</TableCell>
                                            <TableCell>{row.cause || '-'}</TableCell>
                                            <TableCell>{row.observation || '-'}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>

            {selectedProject && rows.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={exportToExcel}
                        sx={{ mb: 2 }}
                        disabled={userRole === profiles[3].name}
                    >
                        Exportar informe
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default ConsolidatedUnproductivity;
